import { AbstractControl, ValidationErrors } from '@angular/forms'
import { UserRolesEnum } from '@isl-core/enums/user-roles.enum'
import { UserService } from '@isl-core/services/user.service'
import { EmployeesInterface, EmployeeTypesInterface } from '@isl-features/employees/interfaces/employees.interface'
import { LeavesInterface, LeaveTypesInterface } from '@isl-features/employees/interfaces/leaves.interface'
import { SkillsInterface } from '@isl-features/employees/interfaces/skills.interface'
import { LabourSheetStatusEnum } from '@isl-features/labour-sheets/enums/labour-sheets.enum'

export const assignEmployeeType = (employees: EmployeesInterface[], types: EmployeeTypesInterface[]) => {
    employees.map((employee) => {
        const employeeType = types.find((type) => type.id === employee.employeeTypeId)
        employee.employeeType = employeeType?.name
    })

    return employees
}

export const assignEmployeeFullName = (employees: EmployeesInterface[]) => {
    employees.map((employee) => {
        employee.fullName = employee.firstName + ' ' + employee.lastName
    })

    return employees
}

export const assignSkills = (employees: EmployeesInterface[], skills: SkillsInterface[]) => {
    employees.map((employee) => {
        const employeeSkills = skills.filter((skill) => employee.employeeSkills.includes(skill.id))
        employee.employeeSkillsNames = employeeSkills.map((skill) => ' ' + skill.name)
    })
    return employees
}

export const firstLastNameSort = (order: string) => {
    return [`firstName,${order}`, `lastName,${order}`]
}

export const labourSheetSort = (order: string) => {
    return [`sortingDate,${order}`, `startTime,${order}`, `modifiedDate,${order}`]
}

export function getLabourSheetStatus(status: string | number | null): LabourSheetStatusEnum | null {
    if (typeof status === 'string') {
        const lowerCaseStatus = status.toLowerCase()
        const matchingKey = Object.keys(LabourSheetStatusEnum).find((key) => key.toLowerCase() === lowerCaseStatus)
        if (matchingKey) {
            return LabourSheetStatusEnum[matchingKey as keyof typeof LabourSheetStatusEnum]
        }
    }

    const numericStatus = Number(status)
    if (Object.values(LabourSheetStatusEnum).includes(numericStatus)) {
        return numericStatus as LabourSheetStatusEnum
    }

    return null
}

export const sortByProperty = <T>(values: T[], property: string, order: number): T[] => {
    values.sort((data1, data2) => {
        const value1 = data1[property as keyof T]
        const value2 = data2[property as keyof T]
        let result = 0

        if (value1 == null && value2 != null) result = -1
        else if (value1 != null && value2 == null) result = 1
        else if (value1 == null && value2 == null) result = 0
        else if (typeof value1 === 'string' && typeof value2 === 'string') result = value1.localeCompare(value2)
        else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0

        return result
    })

    if (order === -1) {
        values.reverse()
    }

    return values
}

export const paginateRecords = <T>(records: T[], page: number, rowsPerPage: number): T[] => {
    return records.length && records.length > rowsPerPage
        ? records.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
        : records
}

export const filterRecords = <T>(records: T[], filter: string, fields: string[]): T[] => {
    if (filter === '') {
        return records
    }

    const output: T[] = []
    records.map((item) => {
        for (const field of fields) {
            const fieldValue: string | undefined = item[field as keyof T]?.toString()

            if (
                fieldValue !== null &&
                fieldValue !== undefined &&
                fieldValue.length !== 0 &&
                fieldValue.toLowerCase().includes(filter.trim().toLowerCase())
            ) {
                output.push(item)
                break
            }
        }
    })

    return output
}

export const utf8Validator = (control: AbstractControl): ValidationErrors | null => {
    const normalizedValue = control.value.normalize('NFC')

    const isValidUtf8 = Array.from(normalizedValue as string).every((char: string) => {
        const codePoint = char.codePointAt(0)
        return codePoint !== undefined && codePoint !== null && codePoint <= 0x10ffff
    })

    if (!isValidUtf8) {
        return { utf8Only: true }
    }

    return null
}

export const assignEmployeeToLeave = (leaves: LeavesInterface[], employees: EmployeesInterface[]) => {
    leaves.map((leave) => {
        const employee = employees.find((employee) => employee.id === leave.employeeId)
        leave.employeeName = employee?.firstName + ' ' + employee?.lastName
    })

    return leaves
}

export const assignTypeToLeave = (leaves: LeavesInterface[], leaveTypes: LeaveTypesInterface[]) => {
    leaves.map((leave) => {
        const leaveType = leaveTypes.find((leaveType) => leaveType.id === leave.leaveTypeId)
        leave.typeName = leaveType?.name
    })

    return leaves
}

export const hasValue = (value: string): boolean => {
    return value !== null && value !== '' && value !== undefined
}

export const isEmployeePermanent = (employee: EmployeesInterface, types: EmployeeTypesInterface[]): boolean => {
    const employeeType = types.find((type) => type.id === employee.employeeTypeId)
    return !employeeType?.name.toLowerCase().startsWith('casual') || false
}

export const printContent = (iframe: string, content: string) => {
    const printFrame = document.getElementById(iframe) as HTMLIFrameElement
    if (printFrame && printFrame.contentWindow) {
        const doc = printFrame.contentWindow.document
        doc.open()
        doc.appendChild(doc.createElement('html')).innerHTML = content

        const images = doc.images
        const imagesLength = images.length
        let counter = 0

        for (let i = 0; i < imagesLength; i++) {
            images[i].onload = () => {
                counter++
                if (counter === imagesLength) {
                    if (printFrame.contentWindow) {
                        printFrame.contentWindow.print()
                    }
                }
            }
        }

        if (imagesLength === 0) {
            setTimeout(() => {
                if (printFrame.contentWindow) printFrame.contentWindow.print()
            }, 0)
        }
    }
}

export const getUserCanEdit = (status: LabourSheetStatusEnum, userService: UserService, action = 'edit'): boolean => {
    let canEdit = false
    if (
        (status === LabourSheetStatusEnum.Draft &&
            !userService.hasAnyRole([UserRolesEnum.RestrictedForeman, UserRolesEnum.Foreman])) ||
        status === LabourSheetStatusEnum.Pending ||
        (status === LabourSheetStatusEnum.Completed && userService.hasRoles([UserRolesEnum.LabourOffice])) ||
        (status === LabourSheetStatusEnum.Closed && userService.hasRoles([UserRolesEnum.Payroll])) ||
        userService.hasAnyRole([UserRolesEnum.Payroll]) ||
        action === 'add'
    ) {
        canEdit = true
    }

    return canEdit
}
